import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import toothManager from "../data/manager/toothManager";
import { TOOTH_GUM_TYPE } from "../data/constant/toothConstant";
import { ArchformShortcutKeyManager } from "./ArchformShortcutKeyManager";

const resolveByteStageList = (byteStageList, isCombine, upperOrLower) => {
  const attrName = `${isCombine ? "combine" : "separate"}${
    upperOrLower ? "UpperStage" : "LowerStage"
  }`;
  return byteStageList?.[attrName] || [];
};

const getAdjacentToothNumber = (list, selectedToothNumber, isPrevious = false) => {
  if (list.length === 0 || typeof selectedToothNumber !== "number") return null;
  const selectedIndex = list.findIndex(
    (item) => Number(item.toothNumber) === selectedToothNumber
  );
  if (selectedIndex < 0) {
    return null;
  }
  if (isPrevious) {
    const prevIndex = selectedIndex - 1;
    if (prevIndex < 0) return null;
    return Number(list[prevIndex].toothNumber);
  }
  const nextIndex = selectedIndex + 1;
  if (nextIndex > list.length - 1) return null;
  return Number(list[nextIndex].toothNumber);
};

const getClosestToothByIndex = (list, sourceToothIndex) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.reduce((closest, item) => {
    if (!closest) return item;
    const currentDistance = Math.abs(item.toothIndex - sourceToothIndex);
    const closestDistance = Math.abs(closest.toothIndex - sourceToothIndex);
    return currentDistance < closestDistance ? item : closest;
  }, null);
};

const getCorrespondingToothNumber = (toothNumber) => {
  const number = Number(toothNumber);
  if (!Number.isInteger(number)) return null;
  const quadrant = Math.floor(number / 10);
  const indexInQuadrant = number % 10;
  if (indexInQuadrant < 1 || indexInQuadrant > 8) return null;
  const quadrantMap = {
    1: 4,
    2: 3,
    3: 2,
    4: 1,
  };
  const targetQuadrant = quadrantMap[quadrant];
  if (!targetQuadrant) return null;
  return targetQuadrant * 10 + indexInQuadrant;
};

export const useShortcutKeyActions = (params) => {
  const {
    currentDataTool,
    keyframeNumber,
    setKeyframeNumber,
    timelinePlaying,
    setTimelinePlaying,
    setView,
    gridCheckboxValue,
    setGridCheckboxValue,
    IPRCheckboxValue,
    setIPRCheckboxValue,
    collisionCheckboxValue,
    setCollisionCheckboxValue,
    overlayCheckboxValue,
    setOverlayCheckboxValue,
    gumCheckboxValue,
    setGumCheckboxValue,
    attachmentCheckboxValue,
    setAttachmentCheckboxValue,
    setCurrentClickItemId,
    currentClickItemId,
    currentClickInitItemRef,
    cameraControlRef,
    setUpperOrLower,
    upperOrLower,
    sliderVal,
    setSliderValue,
    isCombine,
    stageList,
    byteStageList,
    enableShortcutKeyFeature = true,
  } = params;

  const managerRef = useRef(new ArchformShortcutKeyManager());
  const keyframeNumberRef = useRef(keyframeNumber);

  const isToothVisibleAtCurrentStage = useCallback(
    (toothModel) => {
      if (!toothModel) return false;
      try {
        const stageNumber = keyframeNumber + 1;
        const visible =
          toothManager.getToothVisibleByStageNumberAndToothGumTypeAndToothIndex(
            stageNumber,
            toothModel.toothGumType,
            toothModel.toothIndex
          );
        return Number(visible) === 1;
      } catch (error) {
        // Keep shortcuts available when visibility data has not been initialized yet.
        return true;
      }
    },
    [keyframeNumber]
  );

  const buildGroupedToothModel = useCallback((excludeExtracted = false) => {
    const toothModelList = toothManager.getToothModelList() || [];
    const upper = [];
    const lower = [];
    toothModelList.forEach((item) => {
      if (excludeExtracted && !isToothVisibleAtCurrentStage(item)) {
        return;
      }
      if (item.toothGumType === TOOTH_GUM_TYPE.UPPER) {
        upper.push(item);
      } else if (item.toothGumType === TOOTH_GUM_TYPE.LOWER) {
        lower.push(item);
      }
    });
    upper.sort((a, b) => a.toothIndex - b.toothIndex);
    lower.sort((a, b) => a.toothIndex - b.toothIndex);
    return { upper, lower };
  }, [isToothVisibleAtCurrentStage]);

  const stageNumberList = useMemo(() => {
    if (currentDataTool.key === 1) {
      return resolveByteStageList(byteStageList, isCombine, upperOrLower);
    }
    const keys = Object.getOwnPropertyNames(stageList || {});
    if (keys.length === 0) return [];
    return keys
      .map((name) => Number(name.replace("stage", "")))
      .filter((item) => !Number.isNaN(item))
      .sort((a, b) => a - b)
      .map((num) => num - 1);
  }, [currentDataTool.key, byteStageList, stageList, isCombine, upperOrLower]);

  const stageCount = stageNumberList.length;
  const lastStageIndex = stageCount > 0 ? stageCount - 1 : 0;

  const jumpToStage = useCallback(
    (stageIndex) => {
      if (stageCount === 0) return;
      const safeIndex = Math.max(0, Math.min(stageIndex, lastStageIndex));
      setKeyframeNumber(safeIndex);
      keyframeNumberRef.current = safeIndex;
    },
    [lastStageIndex, setKeyframeNumber, stageCount]
  );

  const setActiveTooth = useCallback(
    (targetToothNumber) => {
      const safeToothNumber = Number(targetToothNumber);
      if (!Number.isFinite(safeToothNumber)) return;
      setCurrentClickItemId(safeToothNumber);
      if (currentDataTool.key === 1) {
        setUpperOrLower(safeToothNumber < 30);
      }
    },
    [currentDataTool.key, setCurrentClickItemId, setUpperOrLower]
  );

  const setViewCenterTooth = useCallback(() => {
    if (!cameraControlRef || !currentClickInitItemRef) return;
    const worldPosition = new THREE.Vector3();
    currentClickInitItemRef.getWorldPosition(worldPosition);
    cameraControlRef.setTarget(
      worldPosition.x,
      worldPosition.y,
      worldPosition.z,
      true
    );
  }, [cameraControlRef, currentClickInitItemRef]);

  const navigateTooth = useCallback(
    (isPrevious) => {
      const selectedToothNumber = Number(currentClickItemId);
      if (!Number.isFinite(selectedToothNumber)) return;
      const groupedToothModelAll = buildGroupedToothModel(false);
      const groupedToothModelVisible = buildGroupedToothModel(true);
      const selectedToothModel = [...groupedToothModelAll.upper, ...groupedToothModelAll.lower].find(
        (item) => Number(item.toothNumber) === selectedToothNumber
      );
      if (!selectedToothModel) return;
      const activeType = selectedToothModel.toothGumType;
      const navigateAsPrevious =
        activeType === TOOTH_GUM_TYPE.LOWER ? !isPrevious : isPrevious;
      const allList =
        activeType === TOOTH_GUM_TYPE.UPPER
          ? groupedToothModelAll.upper
          : groupedToothModelAll.lower;
      const visibleList =
        activeType === TOOTH_GUM_TYPE.UPPER
          ? groupedToothModelVisible.upper
          : groupedToothModelVisible.lower;
      if (visibleList.length === 0) return;
      const targetToothNumber = getAdjacentToothNumber(
        visibleList,
        selectedToothNumber,
        navigateAsPrevious
      );
      if (targetToothNumber != null) {
        setActiveTooth(targetToothNumber);
        return;
      }
      const selectedIndexInAll = allList.findIndex(
        (item) => Number(item.toothNumber) === selectedToothNumber
      );
      if (selectedIndexInAll < 0) return;
      const selectedToothIndex = allList[selectedIndexInAll].toothIndex;
      const candidateList = visibleList.filter((item) =>
        navigateAsPrevious
          ? item.toothIndex < selectedToothIndex
          : item.toothIndex > selectedToothIndex
      );
      if (candidateList.length === 0) return;
      const fallbackTooth = navigateAsPrevious
        ? candidateList[candidateList.length - 1]
        : candidateList[0];
      setActiveTooth(Number(fallbackTooth.toothNumber));
    },
    [
      buildGroupedToothModel,
      currentClickItemId,
      setActiveTooth,
    ]
  );

  const switchArch = useCallback(() => {
    const selectedToothNumber = Number(currentClickItemId);
    if (!Number.isFinite(selectedToothNumber)) return;
    const groupedToothModelAll = buildGroupedToothModel(false);
    const groupedToothModelVisible = buildGroupedToothModel(true);
    const selectedToothModel = [...groupedToothModelAll.upper, ...groupedToothModelAll.lower].find(
      (item) => Number(item.toothNumber) === selectedToothNumber
    );
    if (!selectedToothModel) return;
    const sourceType = selectedToothModel.toothGumType;
    const targetType =
      sourceType === TOOTH_GUM_TYPE.UPPER
        ? TOOTH_GUM_TYPE.LOWER
        : TOOTH_GUM_TYPE.UPPER;
    const sourceList =
      sourceType === TOOTH_GUM_TYPE.UPPER
        ? groupedToothModelAll.upper
        : groupedToothModelAll.lower;
    const targetAllList =
      targetType === TOOTH_GUM_TYPE.UPPER
        ? groupedToothModelAll.upper
        : groupedToothModelAll.lower;
    const targetVisibleList =
      targetType === TOOTH_GUM_TYPE.UPPER
        ? groupedToothModelVisible.upper
        : groupedToothModelVisible.lower;
    if (targetVisibleList.length === 0) return;

    let targetToothNumber = targetVisibleList[0].toothNumber;
    const correspondingToothNumber = getCorrespondingToothNumber(selectedToothNumber);
    const exactCorrespondingTooth = correspondingToothNumber == null
      ? null
      : targetVisibleList.find(
        (item) => Number(item.toothNumber) === Number(correspondingToothNumber)
      );
    if (exactCorrespondingTooth) {
      targetToothNumber = exactCorrespondingTooth.toothNumber;
    } else if (sourceList.length > 0) {
      const correspondingToothInAll = correspondingToothNumber == null
        ? null
        : targetAllList.find(
          (item) => Number(item.toothNumber) === Number(correspondingToothNumber)
        );
      const sourceIndexInArch = sourceList.findIndex(
        (item) => Number(item.toothNumber) === Number(selectedToothModel.toothNumber)
      );
      const sourceToothIndex = correspondingToothInAll
        ? correspondingToothInAll.toothIndex
        : (sourceIndexInArch >= 0
          ? sourceList[sourceIndexInArch].toothIndex
          : selectedToothModel.toothIndex);
      const closestTarget = getClosestToothByIndex(
        targetVisibleList,
        sourceToothIndex
      );
      if (closestTarget) {
        targetToothNumber = closestTarget.toothNumber;
      }
    }

    setActiveTooth(targetToothNumber);
    if (currentDataTool.key === 1) {
      setUpperOrLower(targetType === TOOTH_GUM_TYPE.UPPER);
    }
  }, [
    buildGroupedToothModel,
    currentClickItemId,
    currentDataTool.key,
    setActiveTooth,
    setUpperOrLower,
  ]);

  const toggleTimelinePlay = useCallback(() => {
    if (stageCount <= 1) return;
    if (typeof setTimelinePlaying !== "function") return;
    setTimelinePlaying(!timelinePlaying);
  }, [setTimelinePlaying, stageCount, timelinePlaying]);

  const toggleUpperArchVisibility = useCallback(() => {
    if (typeof setSliderValue !== "function") return;
    const currentSlider = Number(sliderVal) || 0;
    setSliderValue(currentSlider >= 100 ? 0 : 100);
  }, [setSliderValue, sliderVal]);

  const toggleLowerArchVisibility = useCallback(() => {
    if (typeof setSliderValue !== "function") return;
    const currentSlider = Number(sliderVal) || 0;
    setSliderValue(currentSlider <= -100 ? 0 : -100);
  }, [setSliderValue, sliderVal]);

  useEffect(() => {
    keyframeNumberRef.current = keyframeNumber;
  }, [keyframeNumber]);

  useEffect(() => {
    managerRef.current.setCommands({
      setViewFront: () => setView("front"),
      setViewBack: () => setView("back"),
      setViewRight: () => setView("right"),
      setViewLeft: () => setView("left"),
      setViewUpper: () => setView("upper"),
      setViewLower: () => setView("lower"),
      toggleUpperArchVisibility: () => toggleUpperArchVisibility(),
      toggleLowerArchVisibility: () => toggleLowerArchVisibility(),
      centerTooth: () => setViewCenterTooth(),
      toggleGrid: () => setGridCheckboxValue(!gridCheckboxValue),
      toggleIPR: () => setIPRCheckboxValue(!IPRCheckboxValue),
      toggleCollision: () => setCollisionCheckboxValue(!collisionCheckboxValue),
      toggleSuperimposition: () =>
        setOverlayCheckboxValue(!overlayCheckboxValue),
      toggleGum: () => setGumCheckboxValue(!gumCheckboxValue),
      toggleAttachment: () =>
        setAttachmentCheckboxValue(!attachmentCheckboxValue),
      toggleTimelinePlay: () => toggleTimelinePlay(),
      toInitialStage: () => jumpToStage(0),
      toFinalStage: () => jumpToStage(lastStageIndex),
      toPreviousStage: () => jumpToStage(keyframeNumberRef.current - 1),
      toNextStage: () => jumpToStage(keyframeNumberRef.current + 1),
      nextTooth: () => navigateTooth(false),
      previousTooth: () => navigateTooth(true),
      switchArch: () => switchArch(),
    });
  }, [
    IPRCheckboxValue,
    attachmentCheckboxValue,
    collisionCheckboxValue,
    gumCheckboxValue,
    gridCheckboxValue,
    jumpToStage,
    lastStageIndex,
    navigateTooth,
    overlayCheckboxValue,
    setAttachmentCheckboxValue,
    setCollisionCheckboxValue,
    setGridCheckboxValue,
    setGumCheckboxValue,
    setIPRCheckboxValue,
    setOverlayCheckboxValue,
    setSliderValue,
    setTimelinePlaying,
    setView,
    setViewCenterTooth,
    switchArch,
    toggleLowerArchVisibility,
    toggleTimelinePlay,
    toggleUpperArchVisibility,
  ]);

  useEffect(() => {
    if (!enableShortcutKeyFeature) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      managerRef.current.handleKeyDown(event);
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [enableShortcutKeyFeature]);
};

